// MIDAS V3 — History row (Phase 5)
//
// Port of `HistoryRow` from history-page.jsx (lines 77-121). 3-column layout:
//   1. Time + msg count (56px fixed)
//   2. Title + preview (flex-1)
//   3. Tag pill + arrow (auto)
//
// Preview only renders if backend returned `last_message_preview` (Phase 4
// locked decision). Hover row -> bg var(--midas-v3-card-2).
//
// Click -> onOpen(threadId). Caller dispatches loadThread + navigates.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React from "react";

import { Thread } from "../../../midasPageModels";
import HistoryTagPillV3 from "./HistoryTagPillV3";

interface MidasHistoryRowV3Props {
  thread: Thread;
  /** Resolved machine display name ("Mazak QT-200") or null for platform threads. */
  machineLabel: string | null;
  /** Currently-loaded thread id (or null) — drives the active row marker. */
  activeThreadId: string | null;
  /** Click handler — opens the thread. */
  onOpen: (threadId: string) => void;
  /** Localised "PLATFORM" label for the platform-variant tag pill. */
  platformLabel: string;
  /** Localised "msgs" suffix for the message-count sub-line. */
  msgsLabel: string;
  /**
   * Index within the parent's group for staggered entry. We don't need a
   * shared keyframe for this — using a tiny inline transition delay on
   * `opacity` instead keeps the row palette-aware without depending on
   * `useRedesignSetup` (v3 doesn't load that). Optional, defaults to 0.
   */
  index?: number;
}

/** Format the time column — "HH:MM" only. */
const formatTime = (iso: string): string => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const MidasHistoryRowV3: React.FC<MidasHistoryRowV3Props> = ({
  thread,
  machineLabel,
  activeThreadId,
  onOpen,
  platformLabel,
  msgsLabel,
  index = 0,
}) => {
  const time = formatTime(thread.last_message_at || thread.created_at);
  const msgCountLabel = `${thread.message_count} ${msgsLabel}`;
  const preview = thread.last_message_preview;
  const isActive = activeThreadId !== null && activeThreadId === thread.id;
  const isPlatform = !thread.machine_id;

  // Stagger entry — fade-up via inline animation. Reduced-motion users get an
  // instant render thanks to the global override in midas-v3-tokens.scss.
  const animationStyle: React.CSSProperties = {
    animation: "midas-v3-row-in 280ms ease-out both",
    animationDelay: `${index * 0.035}s`,
  };

  return (
    <button
      type="button"
      onClick={() => onOpen(thread.id)}
      aria-label={`${
        machineLabel || platformLabel
      } — ${thread.title} — ${time} — ${msgCountLabel}`}
      aria-current={isActive ? "true" : undefined}
      title={thread.title}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 18px",
        borderRadius: 10,
        background: "transparent",
        border: "none",
        borderLeft: isActive
          ? "2px solid var(--midas-v3-accent)"
          : "2px solid transparent",
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        transition: "background-color .12s ease, border-color .12s ease",
        width: "100%",
        textAlign: "left",
        fontFamily: "inherit",
        ...animationStyle,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--midas-v3-card-2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
      onFocus={(e) => {
        e.currentTarget.style.background = "var(--midas-v3-card-2)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Left: time + msg count */}
      <div
        className="mono"
        style={{
          width: 56,
          flexShrink: 0,
          textAlign: "left",
        }}
      >
        <div
          style={{
            fontSize: 13,
            color: "var(--midas-v3-ink-2)",
            lineHeight: 1.25,
          }}
        >
          {time}
        </div>
        <div
          style={{
            fontSize: 10.5,
            color: "var(--midas-v3-faint)",
            letterSpacing: "0.04em",
            marginTop: 2,
            lineHeight: 1.2,
          }}
        >
          {msgCountLabel}
        </div>
      </div>

      {/* Title + preview */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14.5,
            fontWeight: 600,
            color: "var(--midas-v3-ink)",
            marginBottom: preview ? 3 : 0,
            letterSpacing: "-0.005em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.25,
          }}
        >
          {thread.title}
        </div>
        {preview && (
          <div
            style={{
              fontSize: 12.5,
              color: "var(--midas-v3-muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.4,
            }}
          >
            {!isPlatform && (
              <span style={{ color: "var(--midas-v3-accent)", marginRight: 6 }}>
                🔧
              </span>
            )}
            {preview}
          </div>
        )}
      </div>

      {/* Tag + arrow */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <HistoryTagPillV3
          kind={isPlatform ? "platform" : "machine"}
          machineLabel={machineLabel}
          platformLabel={platformLabel}
        />
        <span
          aria-hidden="true"
          style={{
            color: "var(--midas-v3-faint)",
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          →
        </span>
      </div>
    </button>
  );
};

export default MidasHistoryRowV3;
